import { createLogger } from '@svs/nova-core/log';
import { getOpenedDraws } from '@svs/nova-poolbetting/core/action/draw';
import type { ReactNode } from 'react';

import type { DrawNumber } from '../../../../../schema/draw/entry.js';
import type { IDefinition } from '../../../../domain/entry.js';
import { DrawClosed } from './DrawClosed.js';

const logger = createLogger('poolbetting.draw-closed-container');

export type DrawClosedContainerProps = {
  /**
   * Content to render when the draw is closed.
   * Can be a ReactNode.
   */
  drawClosedComponent?: ReactNode;
  definition?: IDefinition;
};

/**
 * Renders the draw closed container, displaying the provided component if the draw is closed.
 *
 * @param root0 - The props for the DrawClosedContainer.
 * @param root0.drawClosedComponent - Content to render when the draw is closed.
 * @param root0.definition - The draw definition object containing moduleEngine and other properties.
 * @returns ReactNode
 */
export async function DrawClosedContainer({
  drawClosedComponent,
  definition,
}: DrawClosedContainerProps): Promise<ReactNode> {
  if (!definition?.moduleEngine) {
    logger.warn('Missing definition or moduleEngine');
    return undefined;
  }

  let openedDraws: DrawNumber[] | undefined;
  try {
    const openedDrawProductsResponse = await getOpenedDraws({
      product: definition.moduleEngine,
    });
    if (openedDrawProductsResponse.status === 'ERROR') {
      logger.error('Error fetching opened draws');
      return undefined;
    }

    openedDraws = openedDrawProductsResponse.data.map((round) => round.draw.drawNumber);
  } catch (error) {
    logger.error('Failed to get opened draws', error);
    return undefined;
  }

  return <DrawClosed drawClosedComponent={drawClosedComponent} openedDraws={openedDraws} />;
}
